import type { CSSProperties } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { TuitionCollectionTrendChart } from '../source-faithful/shared/TuitionCollectionTrendChart';
import { TuitionPaymentsReadinessCallout } from '../source-faithful/shared/TuitionPaymentsReadinessCallout';
import { resolveCollectionTrendProjection } from '../tuition/school/collectionTrendProjection';
import {
  ACTIVE_TUITION_ACADEMIC_YEAR,
} from '../tuition/shared/academicYear';
import {
  useGetAdministratorTuitionDashboardQuery,
} from '../../state/domainRouterApi';
import { StatusBadge } from '../school-settings-v2/components/StatusBadge';
import type { StatusBadgeVariant } from '../school-settings-v2/components/StatusBadge';
import { CustomSelect, Icon } from '../school-settings-v2/components/ui';
import { SupportErrorMessage } from '../../components/overlays/EngineeringSupportBanner';
import { RecurringFeeManager } from './RecurringFeeManager';

// ─── Available academic years ─────────────────────────────────────────────────

const buildAvailableAcademicYears = (): string[] => {
  const [startYearStr] = ACTIVE_TUITION_ACADEMIC_YEAR.split('-');
  const startYear = Number(startYearStr);
  return [0, 1, 2].map((offset) => {
    const y = startYear - offset;
    return `${y}-${y + 1}`;
  });
};

const AVAILABLE_ACADEMIC_YEARS = buildAvailableAcademicYears();

// ─── Status filter ────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { label: 'All statuses', value: 'All' },
  { label: 'Current', value: 'Current' },
  { label: 'Overdue', value: 'Overdue' },
  { label: 'Pending', value: 'Pending' },
] as const;

type StatusFilter = 'All' | 'Current' | 'Overdue' | 'Pending';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (cents: number): string =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const formatPercent = (value: number): string => `${Math.round(value * 100)}%`;

function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

const statusBadgeVariant = (statusLabel: string): StatusBadgeVariant => {
  switch (statusLabel) {
    case 'Current':
      return 'success';
    case 'Overdue':
      return 'error';
    default:
      return 'info';
  }
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div
      className="source-faithful-shell"
      style={{ '--ystnj-source-faithful-max-width': '1120px' } as CSSProperties}
    >
      <div className="source-faithful-container">
        <div className="h-16 animate-pulse rounded-xl bg-muted" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="h-28 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
        <div className="h-72 animate-pulse rounded-xl bg-muted" />
        <div className="h-96 animate-pulse rounded-xl bg-muted" />
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function AdministratorTuitionInternalPage() {
  const [selectedYear, setSelectedYear] = useState(ACTIVE_TUITION_ACADEMIC_YEAR);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { data: snapshot, isLoading, isError, refetch } =
    useGetAdministratorTuitionDashboardQuery({
      academicYear: selectedYear,
      familyLimit: 24,
    });

  const filteredFamilies = useMemo(() => {
    if (!snapshot?.families) return [];
    return snapshot.families.filter((family) => {
      const matchesSearch =
        !debouncedSearchQuery ||
        family.familyName.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
      const matchesStatus =
        statusFilter === 'All' || family.statusLabel === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [snapshot?.families, debouncedSearchQuery, statusFilter]);
  const trendData = useMemo(
    () => snapshot == null
      ? []
      : resolveCollectionTrendProjection(
        snapshot,
        new Date().toISOString().slice(0, 10),
      ),
    [snapshot],
  );

  const yearOptions = AVAILABLE_ACADEMIC_YEARS.map((year) => ({ label: year, value: year }));

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div
      data-testid="administrator-tuition-internal-root"
      data-testid-alias="administrator-tuition-source-faithful-root"
      data-route-family="internal-owned"
      data-route-ready="true"
      className="source-faithful-shell"
      style={{ '--ystnj-source-faithful-max-width': '1120px' } as CSSProperties}
    >
      <div className="source-faithful-container">
        {/* Header row */}
        <div className="source-faithful-header">
          <div>
            <h1 className="text-[24px] font-semibold text-irongrey">Tuition management</h1>
            <p className="mt-1 text-[12px] uppercase tracking-[0.05em] text-text-secondary">
              Academic year {selectedYear}
            </p>
          </div>
          <CustomSelect
            testId="administrator-tuition-year-select"
            options={yearOptions}
            value={selectedYear}
            onChange={(value) => setSelectedYear(value)}
          />
        </div>

        {/* Search + status filter row */}
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <div className="relative flex-1 min-w-[200px] max-w-[320px]">
            <input
              ref={searchInputRef}
              data-testid="administrator-tuition-search"
              className="w-full rounded-md border border-border bg-white pl-3 pr-8 py-2 text-sm text-irongrey placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              type="search"
              placeholder="Search families…"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button
                type="button"
                data-testid="administrator-tuition-search-clear"
                aria-label="Clear search"
                onClick={() => {
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-irongrey transition-colors"
              >
                <Icon name="X" className="w-4 h-4" />
              </button>
            ) : null}
          </div>
          <CustomSelect
            testId="administrator-tuition-status-filter"
            options={STATUS_OPTIONS as unknown as { label: string; value: string }[]}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as StatusFilter)}
          />
        </div>

        <TuitionPaymentsReadinessCallout
          testId="administrator-tuition-payments-readiness"
          roleLabel="Administrator"
        />

        {isError ? (
          <SupportErrorMessage
            data-testid="administrator-tuition-dashboard-error"
            className="max-w-3xl"
          >
            <div className="flex items-start gap-4">
              <div className="flex-1">
              <p className="text-sm font-semibold text-irongrey">
                Unable to load tuition administrator dashboard.
              </p>
              <p className="mt-0.5 text-xs text-irongrey/65">
                An error occurred while fetching tuition records.
              </p>
              </div>
              <button
                type="button"
                onClick={() => void refetch()}
                className="inline-flex shrink-0 items-center gap-1.5 rounded border border-border bg-white px-3 py-1.5 text-xs font-medium text-irongrey transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
              >
                <Icon name="RefreshCw" className="h-3.5 w-3.5" />
                Retry
              </button>
            </div>
          </SupportErrorMessage>
        ) : snapshot ? (
          <>
            {/* KPI grid */}
            <div className="source-faithful-kpiGrid">
              {[
                { label: 'Total billed', value: formatCurrency(snapshot.totalBilledCents), variant: 'neutral' as const },
                { label: 'Total collected', value: formatCurrency(snapshot.totalCollectedCents), variant: 'success' as const },
                { label: 'Collection rate', value: formatPercent(snapshot.collectionRate), variant: 'info' as const },
                { label: 'Outstanding', value: formatCurrency(snapshot.outstandingCents), variant: 'warning' as const },
                { label: 'Overdue', value: formatCurrency(snapshot.overdueCents), variant: 'error' as const },
                { label: 'Auto-pay families', value: String(snapshot.autoPayFamilies), variant: 'success' as const },
              ].map((kpi) => (
                <div key={kpi.label} className="rounded-xl border border-border bg-white p-5 shadow-sm">
                  <div className="mb-3">
                    <StatusBadge variant={kpi.variant} label={kpi.label} />
                  </div>
                  <span className="text-[32px] font-semibold leading-tight text-irongrey">
                    {kpi.value}
                  </span>
                </div>
              ))}
            </div>

            <TuitionCollectionTrendChart
              data={trendData}
              title="Tuition trend"
              subtitle={`Billed, collected, due, and outstanding for ${selectedYear}`}
              ariaLabel={`Monthly billed, collected, due, and outstanding tuition for academic year ${selectedYear}, August through July.`}
              testId="administrator-tuition-collection-trend-chart"
            />

            {/* Family accounts table */}
            <div
              data-testid="administrator-tuition-family-accounts-table"
              className="overflow-hidden rounded-[12px] border border-border bg-white shadow-sm"
            >
              <div className="border-b border-border px-6 py-5">
                <h2 className="text-[20px] font-semibold text-irongrey">Family accounts</h2>
              </div>
              <div className="source-faithful-tableScroll">
                <table className="min-w-[980px] w-full">
                  <thead className="bg-muted">
                    <tr className="text-left text-[12px] uppercase tracking-[0.05em] text-text-secondary">
                      {['Family Name', 'Students', 'Billed', 'Paid', 'Balance', 'Payment Methods', 'Status', 'Auto-Pay'].map((label) => (
                        <th key={label} className="px-6 py-3 font-normal">{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFamilies.length > 0 ? (
                      filteredFamilies.map((family) => (
                        <tr
                          key={family.familyId}
                          data-testid={`administrator-tuition-family-row-${family.familyId}`}
                          className="border-t border-border hover:bg-[#f8faf9] transition-colors"
                        >
                          <td className="px-6 py-4 text-[14px] font-semibold text-irongrey">
                            {family.familyName}
                          </td>
                          <td className="px-6 py-4 text-[14px] text-irongrey">
                            {family.studentCount}
                          </td>
                          <td className="px-6 py-4 text-[14px] text-irongrey">
                            {formatCurrency(family.totalChargesCents)}
                          </td>
                          <td className="px-6 py-4 text-[14px] text-irongrey">
                            {formatCurrency(family.totalCreditsCents)}
                          </td>
                          <td className="px-6 py-4 text-[14px] text-irongrey">
                            {formatCurrency(family.netBalanceCents)}
                          </td>
                          <td className="px-6 py-4 text-[14px] text-text-secondary">
                            {family.paymentMethodSummary}
                          </td>
                          <td className="px-6 py-4 text-[14px]">
                            <StatusBadge
                              variant={statusBadgeVariant(family.statusLabel)}
                              label={family.statusLabel}
                            />
                          </td>
                          <td className="px-6 py-4 text-[14px] text-irongrey">
                            {family.autoPayEnrolled ? 'Yes' : 'No'}
                          </td>
                        </tr>
                      ))
                    ) : searchQuery || statusFilter !== 'All' ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-10 text-center">
                          <div
                            data-testid="administrator-tuition-dashboard-empty"
                            className="inline-flex flex-col items-center gap-2"
                          >
                            <Icon name="Search" className="w-8 h-8 text-text-tertiary" />
                            <p className="text-sm font-medium text-text-secondary">
                              No families match the current search or filter.
                            </p>
                            <p className="text-xs text-text-tertiary">
                              Try adjusting your search term or status filter.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-6 py-10 text-center">
                          <div
                            data-testid="administrator-tuition-dashboard-empty"
                            className="inline-flex flex-col items-center gap-3"
                          >
                            <Icon name="Wallet" className="w-10 h-10 text-text-tertiary" />
                            <div>
                              <p className="text-sm font-medium text-text-secondary">
                                No tuition records found for {selectedYear}.
                              </p>
                              <p className="mt-1 text-xs text-text-tertiary">
                                Records may not yet be configured for this academic year.
                                Try selecting a different year or contact support.
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div
            data-testid="administrator-tuition-dashboard-empty"
            className="rounded-xl border border-border bg-muted px-6 py-10 text-center flex flex-col items-center gap-3"
          >
            <Icon name="Wallet" className="w-10 h-10 text-text-tertiary" />
            <div>
              <p className="text-sm font-medium text-text-secondary">
                No tuition records found for {selectedYear}.
              </p>
              <p className="mt-1 text-xs text-text-tertiary">
                Records may not yet be configured for this academic year.
                Try selecting a different year or contact support.
              </p>
            </div>
          </div>
        )}

        <RecurringFeeManager />
      </div>
    </div>
  );
}

// ─── Exported route component ──────────────────────────────────────────────────

export function AdministratorTuitionInternalRoutes(): JSX.Element {
  return (
    <Routes>
      <Route index element={<AdministratorTuitionInternalPage />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
